import { uniqBy } from 'lodash';
import type { Activity, Calendar, Production, Resource } from 'sr-types/lib/search/v1/graphql';
import type { AppDefinition } from '../common/nav/apps';
import { dedupeRecordsById } from './scheduler';
import {
    getUniqueAssignments,
    planningResourceToBryntumResourcesAndCalendars,
    type SchedulerProduction,
    toBryntumDependenciesForActivity,
    toBryntumEventsAndAssignmentsForActivity,
    toBryntumResourceProduction,
    toLineItemsFromProductionActivity
} from './schedulerUtil';

export type BryntumProductionRecord = ReturnType<typeof toBryntumResourceProduction>['production'][number];

export type BryntumResourceRecord = ReturnType<
    typeof planningResourceToBryntumResourcesAndCalendars
>['resources'][number];

export type ProductionSyncMetaEntry = {
    lastFullLoadTimestamp?: Date;
    activityCount: number;
    isComplete: boolean;
};

export type PlanningSchedulerStoreData = {
    lastFetchedAt?: Date;
    eventsFetchedAt?: Date;
    replaceEventIds?: string[];
    events?: Activity[];
    assignments?: {
        event?: string;
        eventId?: string;
        resource?: string;
        resourceId?: string;
    }[];
    dependencies?: {
        from?: { id: string };
        to?: { id: string };
        lag?: number;
        lagUnit?: string;
        type?: string;
    }[];
    resources?: Array<BryntumProductionRecord | BryntumResourceRecord>;
    calendars?: Calendar[];
    fetchMeta?: {
        state: string;
        upadatedAt?: Date;
        completedAt?: Date;
    };
    sync_meta?: Record<string, ProductionSyncMetaEntry>;
};

type ActivityStoreFragmentParams = {
    activities: Activity[];
    application: AppDefinition;
    calendars: Calendar[];
    syncMeta?: Record<string, ProductionSyncMetaEntry>;
    timestamp?: Date;
};

type PlanningStoreDataParams = ActivityStoreFragmentParams & {
    productions: SchedulerProduction[];
    resources: Resource[];
};

export const EMPTY_PLANNING_SCHEDULER_STORE_DATA: PlanningSchedulerStoreData = {
    events: [],
    assignments: [],
    dependencies: [],
    resources: [],
    calendars: [],
    sync_meta: {}
};

export const countActivitiesByProduction = (activities: Activity[] = []) => {
    return activities.reduce<Record<string, number>>((acc, activity) => {
        const productionId = activity?.production?.id;
        if (!productionId) {
            return acc;
        }

        acc[productionId] = (acc[productionId] || 0) + 1;
        return acc;
    }, {});
};

export const buildActivityStoreFragment = ({
    activities,
    application,
    calendars,
    syncMeta,
    timestamp = new Date()
}: ActivityStoreFragmentParams): PlanningSchedulerStoreData => {
    const lineItems = toLineItemsFromProductionActivity(activities, application.name);
    const { events, assignments } = toBryntumEventsAndAssignmentsForActivity(lineItems, application);
    const { dependencies } = toBryntumDependenciesForActivity(lineItems);

    return {
        events,
        assignments,
        dependencies,
        calendars,
        sync_meta: syncMeta,
        lastFetchedAt: timestamp,
        eventsFetchedAt: timestamp
    };
};

export const buildPlanningStoreData = ({
    productions,
    resources,
    activities,
    application,
    calendars,
    syncMeta,
    timestamp
}: PlanningStoreDataParams): PlanningSchedulerStoreData => {
    const { production } = toBryntumResourceProduction(productions);
    const { resources: planningResources } = planningResourceToBryntumResourcesAndCalendars(resources);
    const activityStoreFragment = buildActivityStoreFragment({
        activities,
        application,
        calendars,
        syncMeta,
        timestamp
    });

    return {
        ...activityStoreFragment,
        resources: [...planningResources, ...production]
    };
};

export const buildSyncMetaUpdates = (
    productions: Array<Production | { id?: string; identity?: { id?: string } }>,
    activityCountsByProduction: Record<string, number>,
    isComplete: boolean,
    existingSyncMeta: Record<string, ProductionSyncMetaEntry> = {},
    timestamp?: Date
) => {
    return productions.reduce<Record<string, ProductionSyncMetaEntry>>(
        (acc, production) => {
            const productionId = production.id || production.identity?.id || production.reference?.id;
            if (!productionId) {
                return acc;
            }

            const previousEntry = existingSyncMeta?.[productionId];
            const keepPreviousComplete = Boolean(previousEntry?.isComplete && !isComplete);

            acc[productionId] = {
                lastFullLoadTimestamp: keepPreviousComplete
                    ? previousEntry?.lastFullLoadTimestamp
                    : isComplete
                      ? timestamp || new Date()
                      : previousEntry?.lastFullLoadTimestamp,
                activityCount: activityCountsByProduction?.[productionId] ?? previousEntry?.activityCount ?? 0,
                isComplete: keepPreviousComplete ? true : isComplete
            };

            return acc;
        },
        { ...existingSyncMeta }
    );
};

export const mergePlanningSchedulerStoreData = (
    currentData?: PlanningSchedulerStoreData,
    incomingData?: PlanningSchedulerStoreData
): PlanningSchedulerStoreData => {
    const existingData = currentData || EMPTY_PLANNING_SCHEDULER_STORE_DATA;
    const nextData = incomingData || EMPTY_PLANNING_SCHEDULER_STORE_DATA;
    const allAssignments = [...(nextData.assignments || []), ...(existingData.assignments || [])].filter(Boolean);

    return {
        ...existingData,
        ...nextData,
        events: dedupeRecordsById([...(existingData.events || []), ...(nextData.events || [])]),
        resources: dedupeRecordsById([...(existingData.resources || []), ...(nextData.resources || [])]),
        assignments: getUniqueAssignments(
            allAssignments,
            (assignment) => assignment.eventId !== undefined,
            (assignment) => `${assignment.eventId}-${assignment.resourceId}`
        ),
        dependencies: uniqBy([...(nextData.dependencies || []), ...(existingData.dependencies || [])], 'id'),
        calendars: uniqBy([...(nextData.calendars || []), ...(existingData.calendars || [])], 'id'),
        sync_meta: {
            ...(existingData.sync_meta || {}),
            ...(nextData.sync_meta || {})
        }
    };
};

export const isProductionAvailableInCache = (
    cacheData: PlanningSchedulerStoreData | undefined,
    productionId: string
) => {
    return Boolean(
        cacheData?.sync_meta?.[productionId]?.isComplete &&
        cacheData?.resources?.some((resource) => resource?.id === productionId)
    );
};
